// ─────────────────────────────────────────────────────────────────────────
// Client-side frame extractor for the question editor's "Frames from
// video" picker. The video stream comes from our own /v1/videos/stream
// endpoint which lives behind JWT auth, so a raw <video src="..."> would
// 401 the browser. We work around that with the same blob-url trick the
// entity-image renderer uses: fetch the MP4 via the auth'd helper, convert
// to a Blob URL, point a hidden <video> at it.
//
// CORS isn't a problem with Blob URLs (they're same-origin by definition),
// so we can read pixels off the <canvas> without taint.
// ─────────────────────────────────────────────────────────────────────────

import { getApiBaseUrl } from "../../api/client";

export type ExtractedFrame = {
  tSec: number;
  // data:image/jpeg;base64,... ready to POST to the entity-create endpoint.
  dataUrl: string;
};

// Download the MP4 (auth'd) and return a Blob URL the caller is responsible
// for revoking. Throws if the fetch fails — caller surfaces the error.
export async function loadVideoBlobUrl(
  videoUrl: string,
  token: string,
): Promise<string> {
  // The Generation's `videoUrl` is the full absolute URL the backend already
  // built (e.g. http://localhost:3001/v1/videos/stream/<jobId>.mp4). If for
  // some reason it's relative, prepend the API base.
  const absolute = videoUrl.startsWith("http")
    ? videoUrl
    : `${getApiBaseUrl()}${videoUrl}`;
  let res: Response;
  try {
    res = await fetch(absolute, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    // Network-level failure (DNS / CORS / origin down). The browser's
    // built-in "Failed to fetch" message is useless to the parent — give
    // them something actionable and steer them to the Upload tab.
    throw new Error(
      "Can't reach the video right now. Use the Upload tab instead.",
    );
  }
  if (!res.ok) {
    if (res.status === 502 || res.status === 503 || res.status === 504) {
      // Backend reached but its upstream (Wan) is down — common in local
      // dev when the Cloudflare tunnel has rotated. Same advice.
      throw new Error(
        "The video service is offline. Use the Upload tab instead.",
      );
    }
    throw new Error(`Video load failed (${res.status})`);
  }
  const rawBlob = await res.blob();
  // Force the MIME to video/mp4 regardless of what the upstream
  // /v1/videos/stream/<jobId>.mp4 endpoint returned in Content-Type. Some
  // proxies hand back application/octet-stream (or worse, text/html on a
  // tunnel hiccup) which makes the hidden <video> refuse to even try
  // decoding — and the frame extractor silently produces alt-text-only
  // tiles. Re-wrapping the bytes with an explicit type gives the decoder
  // a fair shot.
  const blob = new Blob([rawBlob], { type: "video/mp4" });
  return URL.createObjectURL(blob);
}

// Pull `count` evenly-spaced still frames from a video Blob URL. The frames
// land at roughly (1/2N, 3/2N, 5/2N, …) of the duration so we don't waste a
// slot on the black opening frame (0s) or the freeze-frame at the very end.
//
// The frames come back as JPEG data URLs (quality 0.75) — small enough to
// POST to the entity endpoint (2MB cap) but legible at the ~150px tile size
// the picker renders them at.
export async function extractFrames(
  blobUrl: string,
  count: number,
): Promise<ExtractedFrame[]> {
  const video = document.createElement("video");
  video.src = blobUrl;
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";

  // Wait for the metadata so we know the duration + intrinsic dimensions.
  await new Promise<void>((resolve, reject) => {
    const onLoaded = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error("Couldn't read video metadata."));
    };
    const cleanup = () => {
      video.removeEventListener("loadedmetadata", onLoaded);
      video.removeEventListener("error", onError);
    };
    video.addEventListener("loadedmetadata", onLoaded);
    video.addEventListener("error", onError);
  });

  const duration = Number.isFinite(video.duration) ? video.duration : 0;
  if (duration <= 0) {
    throw new Error("Video duration unavailable.");
  }

  const w = video.videoWidth || 320;
  const h = video.videoHeight || 180;
  const canvas = document.createElement("canvas");
  // Cap to a sane thumbnail size so the data URL stays small. Long edge
  // 320 keeps the JPEG well under 50 KB for cartoon-style content.
  const maxLong = 320;
  const scale = Math.min(1, maxLong / Math.max(w, h));
  canvas.width = Math.max(1, Math.round(w * scale));
  canvas.height = Math.max(1, Math.round(h * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unsupported.");

  const points: number[] = [];
  for (let i = 0; i < count; i++) {
    points.push(Math.min(duration - 0.05, ((i + 0.5) * duration) / count));
  }

  const frames: ExtractedFrame[] = [];
  for (const tSec of points) {
    // Seek and wait for the `seeked` event before drawing — the browser
    // hasn't actually moved the pixel buffer until then.
    await new Promise<void>((resolve, reject) => {
      const onSeeked = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        reject(new Error("Seek failed"));
      };
      const cleanup = () => {
        video.removeEventListener("seeked", onSeeked);
        video.removeEventListener("error", onError);
      };
      video.addEventListener("seeked", onSeeked);
      video.addEventListener("error", onError);
      video.currentTime = tSec;
    });
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    frames.push({
      tSec: Math.round(tSec),
      dataUrl: canvas.toDataURL("image/jpeg", 0.75),
    });
  }

  return frames;
}
